// ASIN Sales History Service - Direct Firebase Access with Indexed Queries for Performance
import {
  ASINSalesHistoryCompany,
  ASINSalesHistoryResponse,
  OverallSalesHistoryResponse,
  OverallSalesHistoryKPIs,
  OverallSalesHistoryRow
} from '../types/asinSalesHistory';
import { collection, getDocs, doc, getDoc, query, orderBy, onSnapshot, Unsubscribe, limit, where } from 'firebase/firestore';
import { db } from '../config/firebase';

interface ASINSalesHistorySyncStatus {
  status: string; // 'completed', 'running', 'failed'
  completedAt?: any; // Firestore Timestamp
  lastSuccessful?: any; // Firestore Timestamp
  startedAt?: any; // Firestore Timestamp
  triggeredBy?: string; // 'scheduled', 'manual'
  error?: string;
}

class ASINSalesHistoryService {
  private companiesCollection = 'asin_sales_history_companies';
  private dataCollection = 'asin_sales_history';

  async getCompanies(forceReload = false): Promise<ASINSalesHistoryCompany[]> {
    try {
      console.log('🔍 [ASINSalesHistoryService] Fetching companies from Firebase with indexed query...');
      const companiesRef = collection(db, this.companiesCollection);
      // Use indexed query: client (asc) + active (asc) for better performance
      const q = query(
        companiesRef,
        where('active', '==', true),
        orderBy('client', 'asc')
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('📭 [ASINSalesHistoryService] No companies found in Firebase');
        throw new Error('No companies found in database');
      }
      
      const companies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ASINSalesHistoryCompany[];
      
      console.log(`📦 [ASINSalesHistoryService] Found ${companies.length} companies in Firebase`);
      console.log(`🔍 [ASINSalesHistoryService] Companies:`, companies.map(c => `${c.client} (${c.id})`));
      return companies;
    } catch (error) {
      console.error('❌ [ASINSalesHistoryService] Error fetching companies from Firebase:', error);
      throw new Error(`Failed to fetch companies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getASINSalesHistoryData(spreadsheetId: string): Promise<ASINSalesHistoryResponse> {
    try {
      console.log(`🔍 [ASINSalesHistoryService] Fetching data for ${spreadsheetId} from Firebase...`);
      
      // Step 1: Get company info to get company name
      const companyDocRef = doc(db, this.companiesCollection, spreadsheetId);
      const companyDocSnap = await getDoc(companyDocRef);
      
      if (!companyDocSnap.exists()) {
        console.log(`📭 [ASINSalesHistoryService] Company not found for ${spreadsheetId}`);
        throw new Error(`Company not found: ${spreadsheetId}`);
      }
      
      const companyData = companyDocSnap.data();
      const companyName = companyData.client;
      
      // Step 2: Get summary document with KPIs
      const summaryDocRef = doc(db, this.dataCollection, companyName, 'metadata', '_summary');
      const summarySnap = await getDoc(summaryDocRef);
      
      if (!summarySnap.exists()) {
        console.log(`📭 [ASINSalesHistoryService] No data found for ${companyName}`);
        throw new Error(`No data synced for company: ${companyName}`);
      }
      
      const summaryData = summarySnap.data();
      
      // Step 3: Get ASIN documents with indexed query for better performance
      const asinsCollectionRef = collection(db, this.dataCollection, companyName, 'asins');
      // Use indexed query: order by syncedAt (desc) for most recent data first
      const asinsQuery = query(asinsCollectionRef, orderBy('syncedAt', 'desc'));
      const asinsSnap = await getDocs(asinsQuery);
      
      // Convert ASIN documents to rows format (exactly like Days of Cover)
      const rows = asinsSnap.docs.map(doc => {
        const data = doc.data();
        // Ensure date is properly formatted
        let dateStr = data.date || '';
        if (data.date && typeof data.date === 'object' && data.date.toDate) {
          // Handle Firestore Timestamp
          dateStr = data.date.toDate().toISOString().split('T')[0];
        } else if (data.date && typeof data.date === 'object' && data.date.seconds) {
          // Handle Firestore Timestamp object
          dateStr = new Date(data.date.seconds * 1000).toISOString().split('T')[0];
        }
        
        return [
          dateStr,
          data.asin || '',
          String(data.traffic || 0),
          String(data.conversionRate || 0),
          String(data.units || 0),
          String(data.revenue || 0),
          String(data.ppcSpend || 0),
          String(data.grossProfit || 0)
        ];
      });
      
      console.log(`✅ [ASINSalesHistoryService] Found ${rows.length} rows for ${companyName}`);
      console.log(`🔍 [ASINSalesHistoryService] Sample row for ${companyName}:`, rows[0]);
      console.log(`🔍 [ASINSalesHistoryService] All rows for ${companyName}:`, rows);
      console.log(`🔍 [ASINSalesHistoryService] Summary data for ${companyName}:`, summaryData);
      
      // Build KPIs object from summary data (matching expected structure)
      // Handle missing KPIs by calculating from row data if not present in summary
      const kpis = {
        totalActiveASINs: summaryData.totalActiveASINs || new Set(rows.filter(row => row[1]).map(row => row[1])).size,
        averageConversionRate: summaryData.averageConversionRate || null,
        totalUnitsSold: summaryData.totalUnitsSold || rows.reduce((sum, row) => sum + (parseInt(row[4]) || 0), 0),
        totalGrossProfit: summaryData.totalGrossProfit || rows.reduce((sum, row) => sum + (parseFloat(row[7]) || 0), 0),
        totalPPCSpend: summaryData.totalPPCSpend || rows.reduce((sum, row) => sum + (parseFloat(row[6]) || 0), 0),
        percentASINsWithPPCButNoSales: summaryData.percentASINsWithPPCButNoSales || null
      };
      
      console.log(`🔍 [ASINSalesHistoryService] Built KPIs for ${companyName}:`, kpis);

      const response: ASINSalesHistoryResponse = {
        spreadsheetId,
        client: companyName,
        range: '1. ASIN Sales History!A1:H',
        headers: [
          'Date',
          'ASIN',
          'Traffic',
          'Conversion Rate',
          'Units',
          'Revenue',
          'PPC Spend',
          'Gross Profit'
        ],
        rows,
        kpis
      };
      
      return response;
    } catch (error) {
      console.error('❌ [ASINSalesHistoryService] Error fetching data from Firebase:', error);
      throw new Error(`Failed to fetch ASIN sales history data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getOverallSalesHistoryData(spreadsheetId: string): Promise<OverallSalesHistoryResponse> {
    try {
      console.log(`🔍 [ASINSalesHistoryService] Fetching Overall Sales History data for ${spreadsheetId} from Firebase...`);
      
      // Step 1: Get company info
      const companyDocRef = doc(db, this.companiesCollection, spreadsheetId);
      const companyDocSnap = await getDoc(companyDocRef);
      
      if (!companyDocSnap.exists()) {
        console.log(`📭 [ASINSalesHistoryService] Company not found for ${spreadsheetId}`);
        throw new Error(`Company not found: ${spreadsheetId}`);
      }
      
      const companyData = companyDocSnap.data();
      const companyName = companyData.client;
      
      console.log(`📊 [ASINSalesHistoryService] Aggregating Overall Sales data from ASIN data for ${companyName}...`);
      
      // Step 2: Get ASIN documents with indexed query
      const asinsCollectionRef = collection(db, this.dataCollection, companyName, 'asins');
      const asinsQuery = query(asinsCollectionRef, orderBy('syncedAt', 'desc'));
      const asinsSnap = await getDocs(asinsQuery);
      
      // Step 3: Aggregate data by date
      const dailyAggregates = new Map();
      
      asinsSnap.docs.forEach(doc => {
        const data = doc.data();
        
        // Parse date
        let dateStr = data.date || '';
        if (data.date && typeof data.date === 'object' && data.date.toDate) {
          dateStr = data.date.toDate().toISOString().split('T')[0];
        } else if (data.date && typeof data.date === 'object' && data.date.seconds) {
          dateStr = new Date(data.date.seconds * 1000).toISOString().split('T')[0];
        }
        
        if (!dailyAggregates.has(dateStr)) {
          dailyAggregates.set(dateStr, {
            traffic: 0,
            units: 0,
            revenue: 0,
            ppcSpend: 0,
            grossProfit: 0,
            conversionRateSum: 0,
            conversionRateCount: 0
          });
        }
        
        const dayData = dailyAggregates.get(dateStr);
        dayData.traffic += data.traffic || 0;
        dayData.units += data.units || 0;
        dayData.revenue += data.revenue || 0;
        dayData.ppcSpend += data.ppcSpend || 0;
        dayData.grossProfit += data.grossProfit || 0;
        
        // Track conversion rate for averaging
        if (data.conversionRate) {
          dayData.conversionRateSum += data.conversionRate;
          dayData.conversionRateCount += 1;
        }
      });
      
      // Step 4: Convert to rows format
      const rows = Array.from(dailyAggregates.entries()).map(([date, data]) => {
        const avgConversionRate = data.conversionRateCount > 0
          ? data.conversionRateSum / data.conversionRateCount
          : 0;
        
        return [
          date,
          String(data.traffic),
          String(Math.round(avgConversionRate * 100) / 100),
          String(data.units),
          String(Math.round(data.revenue * 100) / 100),
          String(Math.round(data.ppcSpend * 100) / 100),
          String(Math.round(data.grossProfit * 100) / 100)
        ];
      });
      
      // Sort by date (most recent first)
      rows.sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
      
      console.log(`✅ [ASINSalesHistoryService] Aggregated ${rows.length} daily rows for ${companyName}`);
      console.log(`🔍 [ASINSalesHistoryService] Sample overall row:`, rows[0]);
      
      // Step 5: Calculate KPIs
      const totalDays = rows.length;
      const totalUnitsSold = rows.reduce((sum, row) => sum + parseInt(row[3]), 0);
      const totalRevenue = rows.reduce((sum, row) => sum + parseFloat(row[4]), 0);
      const totalGrossProfit = rows.reduce((sum, row) => sum + parseFloat(row[6]), 0);
      const totalPPCSpend = rows.reduce((sum, row) => sum + parseFloat(row[5]), 0);
      const avgConversionRate = rows.reduce((sum, row) => sum + parseFloat(row[2]), 0) / (totalDays || 1);
      const avgDailyRevenue = totalDays > 0 ? totalRevenue / totalDays : 0;
      
      // Determine profit trend
      const firstWeekProfit = rows.slice(-7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / Math.min(7, rows.length);
      const lastWeekProfit = rows.slice(0, 7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / Math.min(7, rows.length);
      let profitTrend: 'increasing' | 'decreasing' | 'stable' = 'stable';
      if (lastWeekProfit > firstWeekProfit * 1.1) profitTrend = 'increasing';
      else if (lastWeekProfit < firstWeekProfit * 0.9) profitTrend = 'decreasing';
      
      const response: OverallSalesHistoryResponse = {
        spreadsheetId,
        client: companyName,
        range: '2. Overall Sales History!A1:G',
        headers: [
          'Date',
          'Traffic',
          'Conversion Rate',
          'Units',
          'Revenue',
          'PPC Spend',
          'Gross Profit'
        ],
        rows,
        kpis: {
          totalDays,
          averageConversionRate: Math.round(avgConversionRate * 100) / 100,
          totalUnitsSold,
          totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
          totalPPCSpend: Math.round(totalPPCSpend * 100) / 100,
          averageDailyRevenue: Math.round(avgDailyRevenue * 100) / 100,
          profitTrend
        }
      };
      
      return response;
    } catch (error) {
      console.error('❌ [ASINSalesHistoryService] Error fetching overall data from Firebase:', error);
      throw new Error(`Failed to fetch overall sales history data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getGlobalOverallSalesHistoryData(): Promise<OverallSalesHistoryResponse> {
    try {
      console.log(`🔍 [ASINSalesHistoryService] Fetching Global Overall Sales History data (no company filter)...`);
      
      // Check if we're in development or production
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        console.log(`🔧 [ASINSalesHistoryService] Development mode: Using mock global data`);
        return this.getMockGlobalOverallSalesHistoryData();
      }
      
      // Production: Fetch all companies and their data from the server
      console.log(`🚀 [ASINSalesHistoryService] Production mode: Fetching data for all companies`);
      
      // Get all active companies
      const companies = await this.getCompanies();
      console.log(`✅ [ASINSalesHistoryService] Found ${companies.length} companies to aggregate`);
      
      // Create a map to store aggregated data by date
      const globalDailyAggregates = new Map();
      
      // Process each company
      for (const company of companies) {
        try {
          console.log(`🔄 [ASINSalesHistoryService] Processing company: ${company.client}`);
          
          // Get company data using our client-side aggregation method
          const companyData = await this.getOverallSalesHistoryData(company.id);
          console.log(`✅ [ASINSalesHistoryService] Got ${companyData.rows.length} rows for ${company.client}`);
          
          // Aggregate this company's data into the global data
          companyData.rows.forEach(row => {
            const [date, traffic, conversionRate, units, revenue, ppcSpend, grossProfit] = row;
            
            if (!globalDailyAggregates.has(date)) {
              globalDailyAggregates.set(date, {
                date,
                traffic: 0,
                units: 0,
                revenue: 0,
                ppcSpend: 0,
                grossProfit: 0,
                conversionEvents: 0,
                companyCount: 0
              });
            }
            
            const dayData = globalDailyAggregates.get(date);
            dayData.traffic += parseInt(traffic) || 0;
            dayData.units += parseInt(units) || 0;
            dayData.revenue += parseFloat(revenue) || 0;
            dayData.ppcSpend += parseFloat(ppcSpend) || 0;
            dayData.grossProfit += parseFloat(grossProfit) || 0;
            
            // Track conversion rate calculation
            const cr = parseFloat(conversionRate) || 0;
            if (cr > 0) {
              dayData.conversionEvents += cr;
              dayData.companyCount += 1;
            }
          });
        } catch (companyError) {
          console.warn(`⚠️ [ASINSalesHistoryService] Error processing company ${company.client}:`, companyError);
          // Continue with other companies
        }
      }
      
      // Convert to rows format
      const globalRows = Array.from(globalDailyAggregates.values()).map(dayData => {
        const avgConversionRate = dayData.companyCount > 0 ? (dayData.conversionEvents / dayData.companyCount) : 0;
        return [
          dayData.date,
          String(dayData.traffic),
          String(Math.round(avgConversionRate * 100) / 100),
          String(dayData.units),
          String(Math.round(dayData.revenue * 100) / 100),
          String(Math.round(dayData.ppcSpend * 100) / 100),
          String(Math.round(dayData.grossProfit * 100) / 100)
        ];
      });
      
      // Sort by date (most recent first)
      globalRows.sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
      
      // Calculate global KPIs
      const totalDays = globalRows.length;
      const totalUnitsSold = globalRows.reduce((sum, row) => sum + (parseInt(row[3]) || 0), 0);
      const totalRevenue = globalRows.reduce((sum, row) => sum + (parseFloat(row[4]) || 0), 0);
      const totalGrossProfit = globalRows.reduce((sum, row) => sum + (parseFloat(row[6]) || 0), 0);
      const totalPPCSpend = globalRows.reduce((sum, row) => sum + (parseFloat(row[5]) || 0), 0);
      const avgConversionRate = globalRows.reduce((sum, row) => sum + (parseFloat(row[2]) || 0), 0) / (totalDays || 1);
      const avgDailyRevenue = totalDays > 0 ? totalRevenue / totalDays : 0;
      
      // Determine profit trend
      const firstWeekProfit = globalRows.slice(-7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / Math.min(7, globalRows.length);
      const lastWeekProfit = globalRows.slice(0, 7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / Math.min(7, globalRows.length);
      let profitTrend: 'increasing' | 'decreasing' | 'stable' = 'stable';
      if (lastWeekProfit > firstWeekProfit * 1.1) profitTrend = 'increasing';
      else if (lastWeekProfit < firstWeekProfit * 0.9) profitTrend = 'decreasing';
      
      console.log(`✅ [ASINSalesHistoryService] Generated ${globalRows.length} global daily rows from ${companies.length} companies`);
      
      return {
        spreadsheetId: 'global-overall',
        client: 'All Companies Combined',
        range: '2. Overall Sales History!A1:G',
        headers: [
          'Date',
          'Traffic',
          'Conversion Rate',
          'Units',
          'Revenue',
          'PPC Spend',
          'Gross Profit'
        ],
        rows: globalRows,
        kpis: {
          totalDays,
          averageConversionRate: Math.round(avgConversionRate * 100) / 100,
          totalUnitsSold,
          totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
          totalPPCSpend: Math.round(totalPPCSpend * 100) / 100,
          averageDailyRevenue: Math.round(avgDailyRevenue * 100) / 100,
          profitTrend
        }
      };
      
    } catch (error) {
      console.error('❌ [ASINSalesHistoryService] Error fetching global overall data:', error);
      throw new Error(`Failed to fetch global overall sales history data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async checkHealth(): Promise<{ status: string; available: boolean }> {
    try {
      // Check if we can connect to Firestore
      const companiesRef = collection(db, this.companiesCollection);
      await getDocs(query(companiesRef));
      return { status: 'connected', available: true };
    } catch (error) {
      console.error('Firestore connection error:', error);
      return { status: 'offline', available: false };
    }
  }

  async triggerSync(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch('/.netlify/functions/trigger-asin-sales-history-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ syncAll: true })
      });
      
      if (!response.ok) {
        throw new Error(`ASIN sync failed: ${response.statusText}`);
      }
      
      await response.json();
      return { success: true, message: 'ASIN sync triggered successfully' };
    } catch (error) {
      console.error('Error triggering ASIN sync:', error);
      return { success: false, message: `ASIN sync failed: ${error}` };
    }
  }

  async triggerOverallSync(): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔄 [ASINSalesHistoryService] Triggering Overall Sales History sync...');
      
      const response = await fetch('/.netlify/functions/trigger-overall-sales-history-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ syncAll: true })
      });
      
      if (!response.ok) {
        throw new Error(`Overall sync failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('✅ [ASINSalesHistoryService] Overall sync result:', result);
      
      return {
        success: true,
        message: result.message || 'Overall Sales History sync triggered successfully'
      };
    } catch (error) {
      console.error('❌ [ASINSalesHistoryService] Error in Overall sync process:', error);
      return {
        success: false,
        message: `Overall sync process error: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  async getSyncStatus(companyId: string): Promise<ASINSalesHistorySyncStatus | null> {
    try {
      const statusRef = doc(db, 'asin_sales_history_sync_status', companyId);
      const statusSnap = await getDoc(statusRef);
      
      if (statusSnap.exists()) {
        return statusSnap.data() as ASINSalesHistorySyncStatus;
      }
      return null;
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return null;
    }
  }

  subscribeSyncStatus(
    companyId: string,
    callback: (status: ASINSalesHistorySyncStatus | null) => void
  ): Unsubscribe {
    const statusRef = doc(db, 'asin_sales_history_sync_status', companyId);
    
    return onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as ASINSalesHistorySyncStatus);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in sync status subscription:', error);
      callback(null);
    });
  }

  // Mock data methods for development/fallback
  private getMockCompanies(): ASINSalesHistoryCompany[] {
    return [
      {
        id: 'mock-company-1',
        client: 'Mock Sales Company 1',
        spreadsheetId: 'mock-spreadsheet-1',
        active: true,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'mock-company-2',
        client: 'Mock Sales Company 2',
        spreadsheetId: 'mock-spreadsheet-2',
        active: true,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];
  }

  private getMockASINSalesHistoryData(spreadsheetId: string): ASINSalesHistoryResponse {
    const mockCompany = this.getMockCompanies().find(c => c.spreadsheetId === spreadsheetId);
    const companyName = mockCompany?.client || 'Mock Company';

    // Generate mock data for the last 30 days
    const rows: string[][] = [];
    const asins = ['B01234ABCD', 'B09876WXYZ', 'B05555MNOP'];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      asins.forEach(asin => {
        const traffic = Math.floor(Math.random() * 500) + 50;
        const conversionRate = (Math.random() * 20 + 5).toFixed(2);
        const units = Math.floor(traffic * (parseFloat(conversionRate) / 100));
        const revenue = units * (Math.random() * 50 + 20);
        const ppcSpend = revenue * (Math.random() * 0.3 + 0.1);
        const grossProfit = revenue - ppcSpend - (units * 10); // Assuming $10 cost per unit

        rows.push([
          dateStr,
          asin,
          String(traffic),
          conversionRate,
          String(units),
          String(Math.round(revenue * 100) / 100),
          String(Math.round(ppcSpend * 100) / 100),
          String(Math.round(grossProfit * 100) / 100)
        ]);
      });
    }

    const totalPPCSpend = rows.reduce((sum, row) => sum + parseFloat(row[6]), 0);
    const totalGrossProfit = rows.reduce((sum, row) => sum + parseFloat(row[7]), 0);
    const totalUnits = rows.reduce((sum, row) => sum + parseInt(row[4]), 0);
    const avgConversionRate = rows.reduce((sum, row) => sum + parseFloat(row[3]), 0) / rows.length;
    
    // Calculate ASINs with PPC spend but no sales
    const asinsWithPPCButNoSales = asins.filter(asin => {
      const asinRows = rows.filter(row => row[1] === asin);
      const totalAsinPPCSpend = asinRows.reduce((sum, row) => sum + parseFloat(row[6]), 0);
      const totalAsinUnits = asinRows.reduce((sum, row) => sum + parseInt(row[4]), 0);
      return totalAsinPPCSpend > 0 && totalAsinUnits === 0;
    });
    
    const kpis = {
      totalActiveASINs: asins.length,
      averageConversionRate: Math.round(avgConversionRate * 100) / 100,
      totalUnitsSold: totalUnits,
      totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
      totalPPCSpend: Math.round(totalPPCSpend * 100) / 100,
      percentASINsWithPPCButNoSales: asins.length > 0 ? Math.round((asinsWithPPCButNoSales.length / asins.length) * 100 * 100) / 100 : null
    };

    return {
      spreadsheetId,
      client: companyName,
      range: '1. ASIN Sales History!A1:H',
      headers: [
        'Date',
        'ASIN',
        'Traffic',
        'Conversion Rate',
        'Units',
        'Revenue',
        'PPC Spend',
        'Gross Profit'
      ],
      rows,
      kpis
    };
  }

  private getMockOverallSalesHistoryData(spreadsheetId: string): OverallSalesHistoryResponse {
    const mockCompany = this.getMockCompanies().find(c => c.spreadsheetId === spreadsheetId);
    const companyName = mockCompany?.client || 'Mock Company';

    // Generate mock overall data for the last 30 days (aggregated from ASIN data)
    const rows: string[][] = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Simulate aggregated data for this date (sum of all ASINs)
      const dailyTraffic = Math.floor(Math.random() * 1500) + 150; // 3 ASINs worth
      const dailyConversionRate = (Math.random() * 15 + 8).toFixed(2); // Average conversion
      const dailyUnits = Math.floor(dailyTraffic * (parseFloat(dailyConversionRate) / 100));
      const dailyRevenue = dailyUnits * (Math.random() * 50 + 25);
      const dailyPPCSpend = dailyRevenue * (Math.random() * 0.25 + 0.15);
      const dailyGrossProfit = dailyRevenue - dailyPPCSpend - (dailyUnits * 12); // $12 avg cost per unit

      rows.push([
        dateStr,
        String(dailyTraffic),
        dailyConversionRate,
        String(dailyUnits),
        String(Math.round(dailyRevenue * 100) / 100),
        String(Math.round(dailyPPCSpend * 100) / 100),
        String(Math.round(dailyGrossProfit * 100) / 100)
      ]);
    }

    const totalPPCSpend = rows.reduce((sum, row) => sum + parseFloat(row[5]), 0);
    const totalGrossProfit = rows.reduce((sum, row) => sum + parseFloat(row[6]), 0);
    const totalUnits = rows.reduce((sum, row) => sum + parseInt(row[3]), 0);
    const totalRevenue = rows.reduce((sum, row) => sum + parseFloat(row[4]), 0);
    const avgConversionRate = rows.reduce((sum, row) => sum + parseFloat(row[2]), 0) / rows.length;
    const avgDailyRevenue = totalRevenue / rows.length;
    
    // Determine profit trend (simple calculation based on first vs last week)
    const firstWeekProfit = rows.slice(0, 7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / 7;
    const lastWeekProfit = rows.slice(-7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / 7;
    let profitTrend: 'increasing' | 'decreasing' | 'stable' = 'stable';
    if (lastWeekProfit > firstWeekProfit * 1.1) profitTrend = 'increasing';
    else if (lastWeekProfit < firstWeekProfit * 0.9) profitTrend = 'decreasing';
    
    const kpis: OverallSalesHistoryKPIs = {
      totalDays: rows.length,
      averageConversionRate: Math.round(avgConversionRate * 100) / 100,
      totalUnitsSold: totalUnits,
      totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
      totalPPCSpend: Math.round(totalPPCSpend * 100) / 100,
      averageDailyRevenue: Math.round(avgDailyRevenue * 100) / 100,
      profitTrend
    };

    return {
      spreadsheetId,
      client: companyName,
      range: '2. Overall Sales History!A1:G',
      headers: [
        'Date',
        'Traffic',
        'Conversion Rate',
        'Units',
        'Revenue',
        'PPC Spend',
        'Gross Profit'
      ],
      rows,
      kpis
    };
  }

  private getMockGlobalOverallSalesHistoryData(): OverallSalesHistoryResponse {
    // Generate mock global overall data for the last 30 days (all companies combined)
    const rows: string[][] = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Simulate global aggregated data for this date (sum of all companies)
      const dailyTraffic = Math.floor(Math.random() * 3000) + 300; // Higher numbers for global data
      const dailyConversionRate = (Math.random() * 12 + 10).toFixed(2); // Global average conversion
      const dailyUnits = Math.floor(dailyTraffic * (parseFloat(dailyConversionRate) / 100));
      const dailyRevenue = dailyUnits * (Math.random() * 45 + 30);
      const dailyPPCSpend = dailyRevenue * (Math.random() * 0.22 + 0.18);
      const dailyGrossProfit = dailyRevenue - dailyPPCSpend - (dailyUnits * 11); // $11 avg cost per unit globally

      rows.push([
        dateStr,
        String(dailyTraffic),
        dailyConversionRate,
        String(dailyUnits),
        String(Math.round(dailyRevenue * 100) / 100),
        String(Math.round(dailyPPCSpend * 100) / 100),
        String(Math.round(dailyGrossProfit * 100) / 100)
      ]);
    }

    const totalPPCSpend = rows.reduce((sum, row) => sum + parseFloat(row[5]), 0);
    const totalGrossProfit = rows.reduce((sum, row) => sum + parseFloat(row[6]), 0);
    const totalUnits = rows.reduce((sum, row) => sum + parseInt(row[3]), 0);
    const totalRevenue = rows.reduce((sum, row) => sum + parseFloat(row[4]), 0);
    const avgConversionRate = rows.reduce((sum, row) => sum + parseFloat(row[2]), 0) / rows.length;
    const avgDailyRevenue = totalRevenue / rows.length;
    
    // Determine profit trend (simple calculation based on first vs last week)
    const firstWeekProfit = rows.slice(0, 7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / 7;
    const lastWeekProfit = rows.slice(-7).reduce((sum, row) => sum + parseFloat(row[6]), 0) / 7;
    let profitTrend: 'increasing' | 'decreasing' | 'stable' = 'stable';
    if (lastWeekProfit > firstWeekProfit * 1.1) profitTrend = 'increasing';
    else if (lastWeekProfit < firstWeekProfit * 0.9) profitTrend = 'decreasing';
    
    const kpis: OverallSalesHistoryKPIs = {
      totalDays: rows.length,
      averageConversionRate: Math.round(avgConversionRate * 100) / 100,
      totalUnitsSold: totalUnits,
      totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
      totalPPCSpend: Math.round(totalPPCSpend * 100) / 100,
      averageDailyRevenue: Math.round(avgDailyRevenue * 100) / 100,
      profitTrend
    };

    return {
      spreadsheetId: 'global-overall',
      client: 'All Companies Combined',
      range: '2. Overall Sales History!A1:G',
      headers: [
        'Date',
        'Traffic',
        'Conversion Rate',
        'Units',
        'Revenue',
        'PPC Spend',
        'Gross Profit'
      ],
      rows,
      kpis
    };
  }
}

export const asinSalesHistoryService = new ASINSalesHistoryService();
export default asinSalesHistoryService;